import { CommonModule } from '@angular/common'
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import * as momentNS from 'moment'
import { BasicInputComponent } from '../basic-input/basic-input.component'

const moment = (momentNS as any).default

@Component({
  selector: 'date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  standalone: true,
  imports: [CommonModule, BasicInputComponent]
})
export class DatePickerComponent implements OnInit {
  // Form control for input field
  @Input() control!: FormControl
  // Label for input field
  @Input() label: string = 'Date of Birth'
  // ID for input field
  @Input() id: string = 'date-picker-input'
  // placeholder for input field
  @Input() placeholder: string = 'dd/mm/yyyy'
  // Flag to show required * for select input
  @Input() isRequired: boolean = false
  // Icon for the input
  @Input() icon: string = ''
  // Input mode
  @Input() mode: BasicInputMode = BasicInputMode.DEFAULT
  // Allow clear button
  @Input() allowClearButton: boolean = true
  // Flag for min date
  @Input() minDate: Date = new Date()
  // Flag for max date
  @Input() maxDate?: Date
  // Emit value for parent component
  @Output() valueChanged: EventEmitter<string> = new EventEmitter<string>()

  // ViewChild to access the basic-input component
  @ViewChild('dateInput', { static: false }) dateInputComponent!: BasicInputComponent

  public basicFieldType = BasicFieldType.DATE
  private _destroyRef = inject(DestroyRef)
  /**
   * Initializes component
   * @returns void
   */
  ngOnInit(): void {
    // Add date validation
    this.addDateValidation()

    if (this.control.value) this.formatDateForInput(this.control.value)

    // Listen for external value changes and format them for the HTML date input
    this.control.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value: string) => {
      if (value && !this.isHtmlDateFormat(value)) this.formatDateForInput(value)
    })
  }

  /**
   * Check if date is in HTML date input format (YYYY-MM-DD)
   * @param value string
   * @returns boolean
   */
  private isHtmlDateFormat(value: string): boolean {
    const htmlDateFormat = /^\d{4}-\d{2}-\d{2}$/
    return htmlDateFormat.test(value)
  }

  /**
   * Format date for input field - convert external formats to YYYY-MM-DD for HTML date input
   * @param value string
   * @returns void
   */
  private formatDateForInput(value: string): void {
    if (!value) return

    // Check if it's already in YYYY-MM-DD format (HTML date input format)
    if (this.isHtmlDateFormat(value)) return

    // Try to parse other date formats and convert to YYYY-MM-DD for the HTML date input
    const parsedDate = moment(value, ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'], true)
    if (parsedDate.isValid()) {
      const htmlDateValue = parsedDate.format('YYYY-MM-DD')
      this.control.setValue(htmlDateValue, { emitEvent: false })
    }
  }

  /**
   * Handle date input change
   * @param event Event
   * @returns void
   */
  public onDateChange(event: Event): void {
    const target = event.target as HTMLInputElement
    const value = target.value // This will be in YYYY-MM-DD format from HTML date input
    // The FormControl should keep the YYYY-MM-DD format for the HTML date input to work
    if (value) this.control.setValue(value, { emitEvent: false })
    else this.control.setValue('')
  }

  /**
   * Handle input blur
   * @param event FocusEvent
   * @returns void
   */
  public onBlur(event: FocusEvent): void {
    this.control.markAsTouched()

    // Emit valueChanged only on blur
    const value = this.control.value
    if (value && this.control.valid) {
      // Emit DD/MM/YYYY format for parent components that expect this format
      const formattedDate = moment(value).format('DD/MM/YYYY')
      this.valueChanged.emit(formattedDate)
    } else if (!value) this.valueChanged.emit('')
  }

  /**
   * Force open the date picker when clicking on the input
   * @returns void
   */
  public openDatePicker(): void {
    if (this.dateInputComponent?.inputElement?.nativeElement) {
      const input = this.dateInputComponent.inputElement.nativeElement
      input.focus()
      input.click()
      if (input.showPicker) input.showPicker()
    }
  }

  /**
   * Get min date as string for HTML date input
   * @returns string
   */
  public getMinDateString(): string {
    if (!this.minDate || isNaN(this.minDate.getTime())) return ''
    return this.minDate.toISOString().split('T')[0]
  }

  /**
   * Get max date as string for HTML date input
   * @returns string
   */
  public getMaxDateString(): string {
    if (!this.maxDate) return ''
    return this.maxDate.toISOString().split('T')[0]
  }

  /**
   * Add date validation to the form control
   * @returns void
   */
  private addDateValidation(): void {
    this.control.addValidators([this.dateValidator()])
    this.control.updateValueAndValidity()
  }

  /**
   * Single date validator for all date rules
   * @returns ValidatorFn
   */
  private dateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null

      const inputDate = moment(control.value, 'YYYY-MM-DD', true)
      if (!inputDate.isValid()) return null

      const today = moment().startOf('day')
      const minDate = moment(this.minDate)

      // Rule 1: No past dates
      if (inputDate.isBefore(today, 'day')) return { invalidDate: true }

      // Rule 2: Min date validation
      if (inputDate.isBefore(minDate, 'day')) return { minDate: { date: minDate.format('DD/MM/YYYY') } }

      // Rule 3: Max date validation (only if maxDate is provided)
      if (this.maxDate && inputDate.isAfter(moment(this.maxDate), 'day')) return { maxDate: { date: moment(this.maxDate).format('DD/MM/YYYY') } }

      return null
    }
  }
}
